/**
 * Local image store for a template's generated/case image — persisted as a FILE,
 * not a cache. On desktop it goes through the native binding's encrypted-at-rest
 * store (AES-256-GCM in app-data; survives app updates, unreadable without the device
 * key). On web (no native binding) it falls back to IndexedDB, which is origin-
 * sandboxed. Either way the generated image stays with the template, decoupled from
 * the cloud, so it never goes "missing" when a template is reopened offline.
 */
import type { EngineBinding } from "@genz/engine-bind";

const DB = "genz-images";
const STORE = "blobs";

function keyFor(templateId: string): string {
  return `tpl:${templateId}`;
}

function idb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

function idbGet(db: IDBDatabase, key: string): Promise<ArrayBuffer | null> {
  return new Promise((resolve) => {
    const r = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    r.onsuccess = () => resolve((r.result as ArrayBuffer) ?? null);
    r.onerror = () => resolve(null);
  });
}

function idbPut(db: IDBDatabase, key: string, val: ArrayBuffer): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => resolve();
    // Surface a write failure (rejects instead of swallowing) so the caller can warn
    // rather than silently lose the template's local image (Task 12 / C4).
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB put failed"));
  });
}

/**
 * Persist the template's case image bytes locally (encrypted on desktop).
 *
 * A store failure now REJECTS (it used to be swallowed here). The caller in the
 * Wizard awaits this inside its own try/catch so the failure is logged + surfaced
 * as a non-blocking warning — it must never fail the save, but it must not be lost
 * silently either. A no-op (no native store + no IndexedDB) resolves normally.
 */
export async function storeTemplateImage(
  engine: EngineBinding,
  templateId: string,
  bytes: Uint8Array,
): Promise<void> {
  if (engine.storeImage) {
    await engine.storeImage(keyFor(templateId), bytes);
    return;
  }
  const db = await idb();
  if (db) {
    const buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
    await idbPut(db, keyFor(templateId), buf as ArrayBuffer);
  }
}

/** Load the template's stored case image bytes, or null if none. */
export async function loadTemplateImageBytes(
  engine: EngineBinding,
  templateId: string,
): Promise<Uint8Array | null> {
  try {
    if (engine.loadImage) {
      return await engine.loadImage(keyFor(templateId));
    }
    const db = await idb();
    if (!db) return null;
    const buf = await idbGet(db, keyFor(templateId));
    return buf ? new Uint8Array(buf) : null;
  } catch {
    return null;
  }
}

/** Load the template's stored case image as an object URL for display, or null. */
export async function loadTemplateImageUrl(
  engine: EngineBinding,
  templateId: string,
): Promise<string | null> {
  const bytes = await loadTemplateImageBytes(engine, templateId);
  if (!bytes || typeof URL === "undefined") return null;
  // Browsers sniff the actual format for <img>/<image>, so a generic type is fine.
  return URL.createObjectURL(new Blob([bytes as BlobPart], { type: "image/png" }));
}

/**
 * Delete every locally-stored template case image (sign-out). On desktop these live in the
 * encrypted native store, on web in IndexedDB; either way they are the previous account's
 * product photography and must not survive into the next account's session.
 * Best-effort — a failure never blocks sign-out.
 */
export async function purgeAllTemplateImages(engine: EngineBinding): Promise<void> {
  // Native store: no enumerate API, so ask the binding to drop its whole image namespace
  // when it can (added alongside deleteImage); otherwise fall through to IndexedDB.
  const withPurge = engine as EngineBinding & { purgeImages?: () => Promise<void> };
  if (withPurge.purgeImages) {
    await withPurge.purgeImages().catch(() => {});
    return;
  }
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onabort = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Delete ONE template's locally-stored case image. Counterpart to
 * {@link storeTemplateImage}, needed when a template is un-synced or deleted: without it
 * the encrypted native record / IndexedDB row outlived the template forever, and nothing
 * ever collected it (multi-MB per template). Best-effort — never throws into the caller.
 */
export async function deleteTemplateImage(
  engine: EngineBinding & { deleteImage?: (key: string) => Promise<void> },
  templateId: string,
): Promise<void> {
  try {
    if (engine.deleteImage) {
      await engine.deleteImage(keyFor(templateId));
      return;
    }
    const db = await idb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).delete(keyFor(templateId));
        tx.oncomplete = () => resolve();
        tx.onabort = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  } catch {
    /* best-effort cleanup — never block an un-sync/delete on it */
  }
}
